import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FloatingDock from '@/components/layout/FloatingDock';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Camera, ArrowLeft, MapPin, Heart, Share2, X, Upload,
  Sparkles, Flame, PartyPopper, Coffee, Zap, Meh, Moon
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Vibe options with icons
const VIBE_OPTIONS = [
  { id: 'poppin', emoji: '🔥', label: "It's Poppin!", color: '#FF6B35', icon: Flame },
  { id: 'chill', emoji: '😎', label: 'Chill Vibes', color: '#4ECDC4', icon: Coffee },
  { id: 'wild', emoji: '🎉', label: 'Getting Wild!', color: '#FF1493', icon: PartyPopper },
  { id: 'lit', emoji: '⚡', label: 'Absolutely Lit', color: '#FFD700', icon: Zap },
  { id: 'cozy', emoji: '✨', label: 'Cozy & Nice', color: '#9B59B6', icon: Sparkles },
  { id: 'mid', emoji: '😐', label: 'Mid TBH', color: '#95A5A6', icon: Meh },
  { id: 'dead', emoji: '💤', label: 'Pretty Dead', color: '#7F8C8D', icon: Moon },
];

const VibeCheck = ({ user }) => {
  const { localeId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [locale, setLocale] = useState(null);
  const [vibeChecks, setVibeChecks] = useState([]);
  const [vibeSummary, setVibeSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Post state
  const [showPostModal, setShowPostModal] = useState(false);
  const [selectedVibe, setSelectedVibe] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [posting, setPosting] = useState(false);
  
  useEffect(() => {
    if (localeId) {
      fetchData();
    }
  }, [localeId]);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [localeRes, checksRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/locale/${localeId}`, { withCredentials: true }),
        axios.get(`${API_URL}/locale/${localeId}/vibe-checks`, { withCredentials: true }),
        axios.get(`${API_URL}/locale/${localeId}/vibe-summary`, { withCredentials: true })
      ]);
      
      setLocale(localeRes.data);
      setVibeChecks(checksRes.data.vibe_checks || []);
      setVibeSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load event');
    } finally {
      setLoading(false);
    }
  };
  
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API_URL}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.url;
    } catch (error) {
      // Fallback to mock URL for demo
      return `https://picsum.photos/seed/${Date.now()}/800/600`;
    }
  };
  
  const handlePostVibeCheck = async () => {
    if (!selectedVibe) {
      toast.error('Select a vibe first!');
      return;
    }
    
    if (!imageFile && !imagePreview) {
      toast.error('Take a photo to share the vibe!');
      return;
    }
    
    setPosting(true);
    try {
      // Upload image
      let imageUrl = imagePreview;
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }
      
      // Post vibe check
      const response = await axios.post(`${API_URL}/locale/${localeId}/vibe-check`, {
        image_url: imageUrl,
        vibe_rating: selectedVibe.id,
        caption: caption,
        tags: [locale?.category || 'event', 'vibecheck']
      }, { withCredentials: true });
      
      toast.success(`${selectedVibe.emoji} Vibe check posted!`);
      setShowPostModal(false);
      setSelectedVibe(null);
      setImageFile(null);
      setImagePreview(null);
      setCaption('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post vibe check');
    } finally {
      setPosting(false);
    }
  };
  
  const handleLikeVibe = async (vibeId) => {
    try {
      await axios.post(`${API_URL}/locale/${localeId}/vibe-check/${vibeId}/like`, {}, { withCredentials: true });
      fetchData();
    } catch (error) {
      toast.error('Failed to like');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900 pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/locale`)}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-bold dark:text-white flex items-center gap-2">
                  <span className="text-2xl">📸</span>
                  Vibe Check
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {locale?.title || 'Loading...'}
                </p>
              </div>
            </div>
            
            <Button
              onClick={() => setShowPostModal(true)}
              className="bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600"
              data-testid="post-vibe-btn"
            >
              <Camera className="w-4 h-4 mr-2" />
              Share Vibe
            </Button>
          </div>
        </div>
      </div>
      
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Vibe Summary Card */}
        {vibeSummary && vibeSummary.total_vibe_checks > 0 && (
          <Card className="p-6 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-200 dark:border-purple-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold dark:text-white">Current Vibe</h3>
              <Badge className="bg-green-500 text-white animate-pulse">
                🔴 LIVE
              </Badge>
            </div>
            
            {vibeSummary.overall_vibe && (
              <div className="flex items-center gap-4 mb-4">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center text-4xl"
                  style={{ backgroundColor: vibeSummary.overall_vibe.color + '30' }}
                >
                  {vibeSummary.overall_vibe.emoji}
                </div>
                <div>
                  <p className="text-2xl font-bold dark:text-white">{vibeSummary.overall_vibe.label}</p>
                  <p className="text-gray-500 dark:text-gray-400">
                    {vibeSummary.total_vibe_checks} people checked in
                  </p>
                </div>
              </div>
            )}
            
            {/* Vibe breakdown bars */}
            <div className="space-y-2">
              {vibeSummary.vibe_breakdown?.slice(0, 3).map(vibe => (
                <div key={vibe.id} className="flex items-center gap-2">
                  <span className="text-lg">{vibe.emoji}</span>
                  <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ 
                        width: `${vibe.percentage}%`,
                        backgroundColor: vibe.color
                      }}
                    />
                  </div>
                  <span className="text-sm text-gray-500 w-12 text-right">{vibe.percentage}%</span>
                </div>
              ))}
            </div>
          </Card>
        )}
        
        {/* Empty State - Prompt to be first */}
        {vibeChecks.length === 0 && (
          <Card className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-500/20 to-orange-500/20 flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">📸</span>
            </div>
            <h3 className="text-xl font-bold mb-2 dark:text-white">Be the first to vibe check!</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Share what's happening at {locale?.title}
            </p>
            <Button
              onClick={() => setShowPostModal(true)}
              className="bg-gradient-to-r from-pink-500 to-orange-500"
            >
              <Camera className="w-4 h-4 mr-2" />
              Post a Vibe Check
            </Button>
          </Card>
        )}
        
        {/* Vibe Checks Feed */}
        {vibeChecks.length > 0 && (
          <div className="space-y-4">
            <h3 className="font-semibold dark:text-white">Latest Vibes</h3>
            
            {vibeChecks.map((vibe, index) => (
              <motion.div
                key={vibe.vibe_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="overflow-hidden">
                  {/* Vibe Image */}
                  <div className="relative aspect-[4/3]">
                    <img 
                      src={vibe.image_url} 
                      alt="Vibe check"
                      className="w-full h-full object-cover"
                    />
                    <div 
                      className="absolute top-3 right-3 px-3 py-1.5 rounded-full text-white font-medium flex items-center gap-1"
                      style={{ backgroundColor: vibe.vibe_color }}
                    >
                      <span>{vibe.vibe_emoji}</span>
                      <span className="text-sm">{vibe.vibe_label}</span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                        {vibe.user?.name?.[0] || vibe.user?.username?.[0] || '?'}
                      </div>
                      <div>
                        <p className="font-medium dark:text-white">{vibe.user?.name || vibe.user?.username}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(vibe.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      {vibe.is_confirmed_attendee && (
                        <Badge className="ml-auto bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          ✓ Attending
                        </Badge>
                      )}
                    </div>
                    
                    {vibe.caption && (
                      <p className="text-gray-700 dark:text-gray-300 mb-3">{vibe.caption}</p>
                    )}
                    
                    {/* Actions */}
                    <div className="flex items-center gap-4 pt-3 border-t border-gray-100 dark:border-gray-800">
                      <button 
                        className="flex items-center gap-1.5 text-gray-500 hover:text-pink-500 transition-colors"
                        onClick={() => handleLikeVibe(vibe.vibe_id)}
                      >
                        <Heart className="w-5 h-5" />
                        <span className="text-sm">{vibe.likes_count || 0}</span>
                      </button>
                      <button className="flex items-center gap-1.5 text-gray-500 hover:text-blue-500 transition-colors">
                        <Share2 className="w-5 h-5" />
                        <span className="text-sm">Share</span>
                      </button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
      
      {/* Post Vibe Check Modal */}
      <AnimatePresence>
        {showPostModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center z-50"
            onClick={() => setShowPostModal(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold dark:text-white">📸 What's the vibe?</h3>
                  <button 
                    onClick={() => setShowPostModal(false)}
                    className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Vibe Selection */}
                <div className="mb-6">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Is it poppin? How's the energy?
                  </p>
                  <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                    {VIBE_OPTIONS.map(vibe => (
                      <button
                        key={vibe.id}
                        onClick={() => setSelectedVibe(vibe)}
                        className={`flex flex-col items-center p-3 rounded-xl transition-all ${
                          selectedVibe?.id === vibe.id
                            ? 'ring-2 ring-offset-2 scale-110'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                        style={{ 
                          ringColor: selectedVibe?.id === vibe.id ? vibe.color : undefined,
                          backgroundColor: selectedVibe?.id === vibe.id ? vibe.color + '20' : undefined
                        }}
                      >
                        <span className="text-2xl mb-1">{vibe.emoji}</span>
                        <span className="text-xs text-gray-600 dark:text-gray-400 text-center leading-tight">
                          {vibe.label.split(' ')[0]}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Photo Upload */}
                <div className="mb-6">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Take a pic! Show what's happening 📷
                  </p>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleImageSelect}
                    className="sr-only"
                  />
                  
                  {imagePreview ? (
                    <div className="relative rounded-xl overflow-hidden">
                      <img src={imagePreview} alt="Preview" className="w-full aspect-video object-cover" />
                      <button
                        onClick={() => {
                          setImageFile(null);
                          setImagePreview(null);
                        }}
                        className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full aspect-video rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 flex flex-col items-center justify-center gap-2 hover:border-primary hover:bg-primary/5 transition-colors"
                    >
                      <Camera className="w-10 h-10 text-gray-400" />
                      <span className="text-gray-500 dark:text-gray-400">Tap to take photo</span>
                    </button>
                  )}
                </div>
                
                {/* Caption */}
                <div className="mb-6">
                  <Textarea
                    placeholder="Add a caption... (optional)"
                    value={caption}
                    onChange={e => setCaption(e.target.value)}
                    rows={2}
                    className="resize-none"
                  />
                </div>
                
                {/* Location */}
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-6 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <MapPin className="w-4 h-4" />
                  <span>Tagging: <strong className="text-gray-700 dark:text-gray-300">{locale?.title}</strong></span>
                </div>
                
                {/* Post Button */}
                <Button
                  onClick={handlePostVibeCheck}
                  disabled={!selectedVibe || !imagePreview || posting}
                  className="w-full h-12 text-lg bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 disabled:opacity-50"
                >
                  {posting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                      Posting...
                    </>
                  ) : (
                    <>
                      {selectedVibe?.emoji || '📸'} Post Vibe Check
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <FloatingDock user={user} />
    </div>
  );
};

export default VibeCheck;
